import axios, { AxiosInstance } from 'axios';
import {
  FeedbackComment,
  FeedbackCommentCreate,
  FeedbackDetail,
  FeedbackListItem,
  FeedbackPayload,
  FeedbackUpdate,
} from './types';

const PATH = '/api/thirdparty/devfeedback';

/**
 * API URL — 각 앱의 httpClient 는 baseURL 이 `/api/v1` 로 끝나 있어 재사용 불가 → 자체 axios 로 절대 URL 호출.
 *  - payrx: VITE_API_URL (origin)
 *  - navido: VITE_API_BASE_URL (`.../api/v1`) 의 `/api/v1` 접미를 제거해 origin 으로 사용
 *      localhost: '/api/v1' → '' (relative · Vite proxy)  /  dev: 'https://dapi.youthnavi.co.kr/api/v1' → origin
 *  - 둘 다 없으면 현재 페이지 origin (SPA 도메인이 /api 를 proxy 하지 않는 dev/prod 에서는 index.html 이
 *    회신되어 리스트 탭이 흰 화면이 되므로 env 값 세팅 필수 — navido 76d5ddd).
 */
const resolveBaseUrl = (): string => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const env = (import.meta as any).env ?? {};
    const envUrl = (env.VITE_API_URL || env.VITE_API_BASE_URL) as string | undefined;
    if (envUrl) return envUrl.replace(/\/$/, '').replace(/\/api\/v1$/, '');
  } catch {
    /* noop */
  }
  if (typeof window !== 'undefined' && window.location) {
    return window.location.origin;
  }
  return '';
};

/** 명시적으로 다른 client 를 사용하고 싶을 때 주입 가능. */
let injected: AxiosInstance | null = null;
export const setDevFeedbackClient = (client: AxiosInstance): void => {
  injected = client;
};

/**
 * 인증 토큰 공급자 — 피드백 API 는 로그인 필수 (Task2073). 관리자 3앱은 'accessToken' 을
 * 로그인 유지 시 localStorage, 아니면 sessionStorage 에 둔다. 다른 곳에 두는 앱(쇼핑몰 zustand)은
 * setDevFeedbackTokenProvider 로 주입한다.
 */
let tokenProvider: () => string | null = () => {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem('accessToken') || window.localStorage.getItem('accessToken');
  } catch {
    return null;
  }
};
export const setDevFeedbackTokenProvider = (provider: () => string | null): void => {
  tokenProvider = provider;
};
const authHeaders = (): Record<string, string> => {
  const token = tokenProvider();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildUrl = (suffix = ''): string => `${resolveBaseUrl()}${PATH}${suffix}`;
const http = (): AxiosInstance | typeof axios => injected ?? axios;

export const submitFeedback = async (
  payload: FeedbackPayload,
  files: File[],
): Promise<{ id: number }> => {
  const fd = new FormData();
  fd.append('payload', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  for (const f of files) fd.append('files', f);
  const res = await http().post(buildUrl(), fd, {
    headers: { 'Content-Type': 'multipart/form-data', ...authHeaders() },
  });
  return { id: res.data.id };
};

/** 내 피드백 id 중 개발 반영 완료 id (비로그인 허용 · id 만 주고받음 · 2026-10-20 lotus 기준) */
export const fetchReflectedIds = async (ids: number[]): Promise<number[]> => {
  if (ids.length === 0) return [];
  const res = await http().get(buildUrl('/reflected'), {
    params: { ids: ids.join(',') },
    headers: authHeaders(),
  });
  return Array.isArray(res.data?.ids) ? res.data.ids : [];
};

export const listFeedback = async (params: {
  appCode?: string;
  env?: string;
  category?: string;
  status?: string;
  closeReason?: string;
  includeDeleted?: boolean;
  q?: string;
  page?: number;
  size?: number;
}): Promise<{
  content: FeedbackListItem[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}> => {
  const res = await http().get(buildUrl(), { params, headers: authHeaders() });
  return res.data;
};

export const getFeedback = async (id: number): Promise<FeedbackDetail> => {
  const res = await http().get(buildUrl(`/${id}`), { headers: authHeaders() });
  return res.data;
};

export const updateFeedback = async (id: number, patch: FeedbackUpdate): Promise<void> => {
  await http().patch(buildUrl(`/${id}`), patch, { headers: authHeaders() });
};

export const listComments = async (id: number): Promise<FeedbackComment[]> => {
  const res = await http().get(buildUrl(`/${id}/comments`), { headers: authHeaders() });
  return res.data;
};

export const addComment = async (id: number, req: FeedbackCommentCreate): Promise<{ id: number }> => {
  const res = await http().post(buildUrl(`/${id}/comments`), req, { headers: authHeaders() });
  return { id: res.data.id };
};

/** 첨부 원본 — 인증 헤더가 필요해 <img src> 로 직접 쓰지 않고 Blob 으로 받는다 (AttachmentImage). */
export const fetchAttachmentBlob = async (attachmentId: number): Promise<Blob> => {
  const res = await http().get(buildUrl(`/attachment/${attachmentId}`), {
    headers: authHeaders(),
    responseType: 'blob',
  });
  return res.data;
};
